/**
 * checus 3DS 弹窗:payermax 的 create3DSPopup 由 SDK 提供,checus 无此能力需自实现。
 * 契约与 payermax dropinInstance.create3DSPopup({ url }) 同形,供 checusDropin 的 handle3DSChallenge 逐字复刻套用。
 * 实现为插入 document.body 的遮罩 + 内嵌 iframe,回调页经 IframeBridge 把结果发回本窗口。
 */
import { createApp } from 'vue'
import { IframeBridge } from '@web-publish-id/shared/helper'
import Checus3DSPopup from '../views/checus/Checus3DSPopup.vue'
import { PaymentCallbackPayload } from '../types/payment-callback'
import { PAYMENT_CALLBACK_URL } from '../const'
import { isPaymentSuccess } from './paymentOrder'

interface Checus3DSResult {
  code: '3DS_PROCESSED'
  data: any
}

interface PopupHandle {
  iframe: HTMLIFrameElement
  destroy: () => void
}

// 旧渠道 status 判成功(与 payermax 一致):toUpperCase() ∈ ['1','SUCCESS']。仅在回调缺 payment_info 时作回落。
function is3DSSuccess(status: unknown): boolean {
  return ['1', 'SUCCESS'].includes(String(status).toUpperCase())
}

// —— 弹窗挂载 ——

// 同一时刻只允许一个 3DS 挑战,重复触发会叠出两层遮罩且前一个的 Promise 永远悬着
let challengeInProgress = false

/**
 * 挂载弹窗到 body 顶层,等组件 onMounted 交出 iframe 后再返回。
 * app.mount 只保证 DOM patch 同步;onMounted 在外层 app 正在 flush 时会被推迟到下个微任务
 * (setupFromAction 从 PayCard3DS.onMounted 同步进入就会撞上),因此必须等 frameMounted,不能同步读。
 */
function mountPopup(url: string, onClose: () => void): Promise<PopupHandle> {
  return new Promise((resolve, reject) => {
    const host = document.createElement('div')
    document.body.appendChild(host)

    const app = createApp(Checus3DSPopup, {
      url,
      onClose,
      onFrameMounted: (el: HTMLIFrameElement) => {
        resolve({ iframe: el, destroy })
      },
    })

    const destroy = () => {
      app.unmount()
      host.remove()
    }

    try {
      app.mount(host)
    } catch (error) {
      destroy()
      reject(error)
    }
  })
}

// —— 对外契约 ——

/**
 * 打开 3DS 弹窗并等待终态。
 * 三种终态:回调成功 → resolve 3DS_PROCESSED;回调失败 status → reject 3DS_FAILED(终态);
 * 用户点关闭且此前无成功消息 → reject USER_CANCEL(可重试)。已有弹窗时 → reject 3DS_BUSY。
 */
export function createChecus3DSPopup({ url }: { url: string }): Promise<Checus3DSResult> {
  if (challengeInProgress) {
    return Promise.reject({ code: '3DS_BUSY', msg: '3DS challenge already in progress' })
  }

  challengeInProgress = true

  return (async () => {
    let settled = false
    const bridge = new IframeBridge()
    let popup: PopupHandle | undefined
    let rejectCancel: ((reason: unknown) => void) | undefined

    // 先让 Vue 收掉自己的 DOM,再拆 bridge(destroy 里的 iframe.remove 此时已是空操作)
    const teardown = () => {
      popup?.destroy()
      bridge.destroy()
      challengeInProgress = false
    }

    try {
      popup = await mountPopup(url, () => {
        if (settled) return
        settled = true
        teardown()
        rejectCancel?.({ code: 'USER_CANCEL' })
      })

      return await new Promise<Checus3DSResult>((resolve, reject) => {
        rejectCancel = reject

        // iframe 里先加载的是第三方 ACS 页,它的 event.source 同样等于 iframe.contentWindow,
        // 只认回调页所在的源才挡得住它伪造 callback 骗出一个成功终态。
        // 取值必须跟 callback_url 同源同一份来源,否则校验会把真回调静默丢掉、Promise 永久悬着。
        bridge.init(popup!.iframe, { verifyOrigin: new URL(PAYMENT_CALLBACK_URL).origin })

        bridge.on('callback', (data: PaymentCallbackPayload) => {
          if (settled) return
          settled = true
          teardown()

          // 优先读支付单 payment_info.payment_status(2 即成功);缺失时(旧渠道回落路径不带该字段)回落旧 status
          const paymentStatus = data?.payment_info?.payment_status
          const success = paymentStatus ? isPaymentSuccess(paymentStatus) : is3DSSuccess(data?.status)

          if (success) {
            resolve({ code: '3DS_PROCESSED', data })
          } else {
            reject({ code: '3DS_FAILED', msg: `3DS failed: ${paymentStatus || data?.status}`, data })
          }
        })
      })
    } catch (error) {
      if (!settled) {
        settled = true
        teardown()
      }
      throw error
    }
  })()
}
